import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/BookFeedback.css';

interface FeedbackResponse {
  body: string;
  admin_name: string;
  responded_at: string;
}

interface FeedbackItem {
  id: string;
  book_id: string;
  book_title: string;
  page: number | null;
  body: string;
  created_at: string;
  status: 'open' | 'answered' | 'resolved';
  response: FeedbackResponse | null;
}

interface Props {
  bookId: string;
  bookTitle: string;
  page?: number;
}

const MAX_LEN = 2000;

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

async function readError(resp: Response): Promise<string> {
  const payload = await resp.json().catch(() => null) as { detail?: unknown } | null;
  const detail = payload?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return `Could not save feedback (HTTP ${resp.status})`;
}

export default function BookFeedback({ bookId, bookTitle, page }: Props) {
  const { user } = useAuth();
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    const token = await user.getIdToken();
    const resp = await fetch(`/api/book-feedback/${encodeURIComponent(bookId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) throw new Error(await readError(resp));
    const data = await resp.json() as { items?: FeedbackItem[] };
    setItems(Array.isArray(data.items) ? data.items : []);
  }, [user, bookId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .catch((err: Error) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      load().catch(() => { /* keep the notes already on screen */ });
    };
    const timer = window.setInterval(refresh, 20000);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [load]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) return;
    const text = body.trim();
    if (!text) {
      setError('Write a note before sending.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`/api/book-feedback/${encodeURIComponent(bookId)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ body: text, page: page && page > 0 ? page : null }),
      });
      if (!resp.ok) throw new Error(await readError(resp));
      setBody('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  const replies = items.filter((item) => item.response);

  return (
    <section className="book-feedback" aria-label={`Feedback for ${bookTitle}`}>
      <header className="book-feedback-head">
        <h2>प्रतिक्रिया · Feedback</h2>
        <p>
          इस पुस्तक पर अपनी बात लिखें। किसी भी व्यवस्थापक का उत्तर यहीं दिखेगा।
          <span>Write about this book. A reply from any admin is shown here.</span>
        </p>
      </header>

      {error && <p className="book-feedback-error" role="alert">{error}</p>}

      {loading && items.length === 0 ? (
        <p className="book-feedback-muted">Loading your notes…</p>
      ) : (
        <div className="book-feedback-list" aria-live="polite">
          {replies.length > 0 && (
            <p className="book-feedback-reply-count">
              {replies.length === 1 ? '1 admin reply' : `${replies.length} admin replies`}
            </p>
          )}
          {items.length === 0 && !loading && (
            <p className="book-feedback-muted">अभी कोई प्रतिक्रिया नहीं · No notes yet.</p>
          )}
          {items.map((item) => (
            <article key={item.id} className={`book-feedback-item book-feedback-item--${item.status}`}>
              <div className="book-feedback-meta">
                <span>{formatWhen(item.created_at)}</span>
                {item.page ? <span>पृष्ठ {item.page} · page {item.page}</span> : null}
              </div>
              <p className="book-feedback-body">{item.body}</p>
              {item.response && (
                <div className="book-feedback-reply">
                  <p className="book-feedback-reply-label">
                    व्यवस्थापक का उत्तर · Admin response
                    <span>{item.response.admin_name} · {formatWhen(item.response.responded_at)}</span>
                  </p>
                  <p className="book-feedback-reply-body">{item.response.body}</p>
                </div>
              )}
              {item.status === 'resolved' && (
                <p className="book-feedback-resolved">समाधान हुआ · Resolved by an admin</p>
              )}
              {item.status === 'open' && (
                <p className="book-feedback-waiting">
                  {item.response
                    ? 'पुनः समीक्षा · Reopened for admin review'
                    : 'उत्तर की प्रतीक्षा · Waiting for an admin response'}
                </p>
              )}
            </article>
          ))}
        </div>
      )}

      <form className="book-feedback-form" onSubmit={submit}>
        <label htmlFor={`book-feedback-${bookId}`}>
          {page ? `पृष्ठ ${page} पर टिप्पणी · Note on page ${page}` : 'टिप्पणी · Your note'}
        </label>
        <textarea
          id={`book-feedback-${bookId}`}
          value={body}
          maxLength={MAX_LEN}
          rows={4}
          placeholder="क्या स्पष्ट रहा, क्या कठिन रहा, या क्या सुधारें…"
          onChange={(event) => setBody(event.target.value)}
        />
        <div className="book-feedback-actions">
          <span className="book-feedback-count">{body.trim().length}/{MAX_LEN}</span>
          <button type="submit" disabled={sending || body.trim().length === 0}>
            {sending ? 'भेजा जा रहा है…' : 'भेजें · Send'}
          </button>
        </div>
      </form>
    </section>
  );
}
